/**
 * Focus One home search dropdown.
 *
 * Adds shortcuts on top of the existing AI search: recent searches, the CU's stocked makes, body
 * styles, and inventory-derived type-ahead. Submitting still posts to the same ?handler=AiSearch
 * form, so a shopper who ignores the dropdown gets exactly the behaviour they had before.
 *
 * Search history is per-browser (localStorage). Nothing is sent anywhere to record it.
 */
const root = document.querySelector<HTMLElement>('[data-home-search]');

if (root) {
    const form = root.querySelector<HTMLFormElement>('form')!;
    const input = root.querySelector<HTMLInputElement>('[data-home-search-input]')!;
    const clearButton = root.querySelector<HTMLElement>('[data-home-search-clear]')!;
    const panel = root.querySelector<HTMLElement>('[data-home-search-panel]')!;
    const results = root.querySelector<HTMLElement>('[data-home-search-results]')!;
    const idle = root.querySelector<HTMLElement>('[data-home-search-idle]')!;
    const historySection = root.querySelector<HTMLElement>('[data-home-search-history]')!;
    const historyList = root.querySelector<HTMLElement>('[data-home-search-history-list]')!;

    const HISTORY_KEY = 'fo-search-history';
    const HISTORY_LIMIT = 5;
    const DEBOUNCE_MS = 180;

    let activeIndex = -1;
    let debounceTimer: number | undefined;
    let requestToken = 0;

    // ---------- history (per browser; storage can throw in private mode) ----------
    const readHistory = (): string[] => {
        try {
            const raw = window.localStorage.getItem(HISTORY_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
        } catch {
            return [];
        }
    };

    const writeHistory = (items: string[]) => {
        try {
            window.localStorage.setItem(HISTORY_KEY, JSON.stringify(items.slice(0, HISTORY_LIMIT)));
        } catch {
            /* storage unavailable — history is a convenience, so carry on without it */
        }
    };

    const rememberSearch = (term: string) => {
        const trimmed = term.trim();
        if (trimmed.length === 0) return;
        const existing = readHistory().filter((x) => x.toLowerCase() !== trimmed.toLowerCase());
        writeHistory([trimmed, ...existing]);
    };

    const renderHistory = () => {
        const items = readHistory();
        historyList.replaceChildren();
        historySection.hidden = items.length === 0;

        items.forEach((term) => {
            const row = document.createElement('li');
            row.className = 'flex max-w-full items-center gap-2';

            const go = document.createElement('button');
            go.type = 'button';
            go.dataset.homeSearchTerm = term;
            go.className =
                'flex min-w-0 items-center gap-2 rounded-[8px] border border-fo-surface-border px-3 py-2 text-left font-mukta-mahee text-sm text-fo-on-surface transition hover:border-fo-primary';
            go.innerHTML =
                '<svg class="size-4 shrink-0 text-fo-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 8v4l3 2"/><path d="M3.05 11a9 9 0 1 1 .5 4"/><path d="M3 4v5h5"/></svg>';
            go.appendChild(document.createTextNode(term));

            const remove = document.createElement('button');
            remove.type = 'button';
            remove.dataset.homeSearchForget = term;
            remove.setAttribute('aria-label', `Remove "${term}" from search history`);
            remove.className = 'shrink-0 rounded-full p-1 text-fo-muted transition hover:text-fo-primary';
            remove.innerHTML =
                '<svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 18L18 6M6 6l12 12"/></svg>';

            row.append(go, remove);
            historyList.appendChild(row);
        });
    };

    // ---------- panel ----------
    const openPanel = () => {
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
    };

    const closePanel = () => {
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        activeIndex = -1;
    };

    const showIdle = () => {
        renderHistory();
        results.hidden = true;
        results.replaceChildren();
        idle.hidden = false;
        activeIndex = -1;
    };

    const optionEls = () => Array.from(results.querySelectorAll<HTMLElement>('[data-home-search-option]'));

    const setActive = (next: number) => {
        const options = optionEls();
        if (options.length === 0) return;

        activeIndex = (next + options.length) % options.length;
        options.forEach((el, i) => {
            const isActive = i === activeIndex;
            el.classList.toggle('bg-fo-selected', isActive);
            el.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        options[activeIndex].scrollIntoView({ block: 'nearest' });
    };

    /** Bolds the part of the phrase the shopper has typed, as the design shows. */
    const buildOptionLabel = (phrase: string, query: string) => {
        const fragment = document.createDocumentFragment();
        const matchesPrefix = phrase.toLowerCase().startsWith(query.toLowerCase()) && query.length > 0;

        if (matchesPrefix) {
            const strong = document.createElement('strong');
            strong.className = 'font-semibold';
            strong.textContent = phrase.slice(0, query.length);
            fragment.append(strong, document.createTextNode(phrase.slice(query.length)));
        } else {
            fragment.appendChild(document.createTextNode(phrase));
        }

        return fragment;
    };

    const renderResults = (phrases: string[], query: string) => {
        idle.hidden = true;
        results.hidden = false;
        results.replaceChildren();
        activeIndex = -1;

        const rowClass =
            'flex w-full cursor-pointer items-center gap-3 rounded-[8px] px-2 py-2.5 text-left font-mukta-mahee text-sm text-fo-on-surface transition hover:bg-fo-selected';
        const icon =
            '<svg class="size-4 shrink-0 text-fo-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>';

        // First row always hands the raw text to the existing AI search.
        const passthrough = document.createElement('li');
        passthrough.innerHTML = `<button type="button" data-home-search-option data-home-search-term="${escapeAttr(query)}" role="option" class="${rowClass}">${icon}<span>Search for &ldquo;<strong class="font-semibold">${escapeHtml(query)}</strong>&rdquo;</span></button>`;
        results.appendChild(passthrough);

        phrases.forEach((phrase) => {
            const item = document.createElement('li');
            const button = document.createElement('button');
            button.type = 'button';
            button.setAttribute('role', 'option');
            button.dataset.homeSearchOption = '';
            button.dataset.homeSearchTerm = phrase;
            button.className = rowClass;
            button.innerHTML = icon;

            const label = document.createElement('span');
            label.appendChild(buildOptionLabel(phrase, query));
            button.appendChild(label);

            item.appendChild(button);
            results.appendChild(item);
        });
    };

    const escapeHtml = (value: string) =>
        value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
    const escapeAttr = escapeHtml;

    const fetchSuggestions = async (query: string) => {
        const token = ++requestToken;
        try {
            const response = await fetch(`/?handler=SearchSuggestions&q=${encodeURIComponent(query)}`, {
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) return;

            const phrases = (await response.json()) as string[];
            // A slower earlier request must not overwrite a newer one's results.
            if (token !== requestToken || input.value.trim() !== query) return;

            renderResults(Array.isArray(phrases) ? phrases : [], query);
        } catch {
            // Network hiccup: leave the passthrough row so search still works.
            if (token === requestToken) renderResults([], query);
        }
    };

    const onInput = () => {
        const query = input.value.trim();
        clearButton.classList.toggle('hidden', query.length === 0);
        openPanel();

        window.clearTimeout(debounceTimer);

        if (query.length < 2) {
            showIdle();
            return;
        }

        debounceTimer = window.setTimeout(() => fetchSuggestions(query), DEBOUNCE_MS);
    };

    const submitWith = (term: string) => {
        input.value = term;
        rememberSearch(term);
        closePanel();
        form.submit();
    };

    // ---------- events ----------
    input.addEventListener('input', onInput);
    input.addEventListener('focus', () => {
        openPanel();
        if (input.value.trim().length < 2) showIdle();
    });

    input.addEventListener('keydown', (event) => {
        if (panel.hidden) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive(activeIndex + 1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive(activeIndex - 1);
        } else if (event.key === 'Enter' && activeIndex >= 0) {
            const option = optionEls()[activeIndex];
            if (option) {
                event.preventDefault();
                submitWith(option.dataset.homeSearchTerm ?? input.value);
            }
        } else if (event.key === 'Escape') {
            closePanel();
        }
    });

    clearButton.addEventListener('click', () => {
        input.value = '';
        clearButton.classList.add('hidden');
        input.focus();
        showIdle();
    });

    // Remember whatever was typed when the form is submitted normally.
    form.addEventListener('submit', () => rememberSearch(input.value));

    root.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;

        const forget = target.closest<HTMLElement>('[data-home-search-forget]');
        if (forget) {
            event.preventDefault();
            const term = forget.dataset.homeSearchForget ?? '';
            writeHistory(readHistory().filter((x) => x !== term));
            renderHistory();
            return;
        }

        const chosen = target.closest<HTMLElement>('[data-home-search-term]');
        if (chosen) {
            event.preventDefault();
            submitWith(chosen.dataset.homeSearchTerm ?? '');
        }
    });

    // pointerdown rather than click: on iOS a click outside can land before a tap on a suggestion
    // is delivered, which would close the panel and swallow the selection.
    document.addEventListener('pointerdown', (event) => {
        if (root.contains(event.target as Node) === false) closePanel();
    });
}
